import React, { useState } from "react";
import { RAIL } from "../../components/terminal/RailTag.jsx";
import { fmtCcy, fmtUSD } from "../../components/terminal/format.js";
import ItemActions, { CTX } from "../../components/terminal/ItemActions.jsx";
import { useVendors, useBills, usePeople, usePayQueue } from "./useTerminalPay.js";
import BulkMode from "./BulkMode.jsx";
import SendPayout from "../payouts/SendPayout.jsx";
import PayoutHistory from "../payouts/PayoutHistory.jsx";

/* Pay tab — mode bar (Vendors / P2P / Bulk / BillPay) over a
   three-column grid: list (left) → composer (center) → queue (right).
   The composer rail-picker, queue rail-mix totals, and Bills
   schedule are all data-driven so wiring to backend B.1–B.4 is
   isolated to the parent providing real arrays. */

const MODES = [
  { id:"vendors", label:"Vendors", hint:"AP · invoices · counterparties",   color:"#4A8CFF" },
  { id:"p2p",     label:"P2P",     hint:"People · contractors · employees", color:"#A78BFA" },
  { id:"bulk",    label:"Bulk",    hint:"CSV · payroll · mass payouts",     color:"#2CCB7E" },
  { id:"bills",   label:"BillPay", hint:"Recurring · utilities · taxes",    color:"#06B6D4" },
  { id:"global",  label:"Global",  hint:"International · PSE · Bre-B · PIX",color:"#F5A524" },
];

function ModeBar({ mode, setMode }) {
  return (
    <div style={{ display:"flex", alignItems:"stretch", borderBottom:"1px solid var(--term-line)", background:"var(--term-bg-pane)" }}>
      {MODES.map((m) => (
        <button key={m.id} onClick={() => setMode(m.id)} style={{
          padding:"10px 22px", background: mode === m.id ? "var(--term-bg-pane-2)" : "transparent",
          border:"none",
          borderRight:"1px solid var(--term-line)",
          borderTop: mode === m.id ? `2px solid ${m.color}` : "2px solid transparent",
          color: mode === m.id ? "var(--term-ink-1)" : "var(--term-ink-3)",
          cursor:"pointer", fontFamily:"inherit", fontSize:12, letterSpacing:"0.04em", fontWeight:600,
          display:"flex", flexDirection:"column", alignItems:"flex-start", gap:1, minWidth:170,
        }}>
          <span style={{ display:"flex", alignItems:"center", gap:8 }}>
            <span style={{ width:6, height:6, background: mode === m.id ? m.color : "var(--term-ink-4)" }} />
            {m.label}
          </span>
          <span style={{ fontSize:9.5, color:"var(--term-ink-3)", fontWeight:400, letterSpacing:"0.06em" }}>{m.hint}</span>
        </button>
      ))}
      <div style={{ flex:1 }} />
    </div>
  );
}

function Composer({ subject, accent, recipientLabel }) {
  /* Selected method index resets to 0 whenever the active subject
     (vendor or person) changes, so picking a different vendor on
     the left doesn't carry over an out-of-range index. */
  const [selectedMethodIdx, setSelectedMethodIdx] = useState(0);
  React.useEffect(() => { setSelectedMethodIdx(0); }, [subject?.channel_id, subject?.handle]);
  return (
    <div className="term-pane">
      <div className="term-pane-head">Compose payment <span className="meta">{subject.name} · {subject.channel_id || subject.handle || ""}</span></div>
      <div style={{ padding:18, display:"flex", flexDirection:"column", gap:16, overflow:"auto" }}>
        <div>
          <div style={{ fontSize:9.5, letterSpacing:"0.16em", color:"var(--term-ink-3)", textTransform:"uppercase", fontWeight:600, marginBottom:8 }}>{recipientLabel}</div>
          <div style={{ display:"flex", alignItems:"center", gap:10, padding:"10px 12px", border:"1px solid var(--term-line)", background:"var(--term-bg-pane)" }}>
            <span className="mono" style={{ fontSize:10.5, color:"var(--term-ink-3)", letterSpacing:"0.04em" }}>{subject.iso}</span>
            <div style={{ flex:1 }}>
              <div style={{ fontSize:13, fontWeight:500 }}>{subject.name}</div>
              <div className="mono" style={{ fontSize:10.5, color:"var(--term-ink-3)" }}>{subject.channel_id || subject.handle || subject.role || ""}</div>
            </div>
          </div>
        </div>
        <div>
          <div style={{ fontSize:9.5, letterSpacing:"0.16em", color:"var(--term-ink-3)", textTransform:"uppercase", fontWeight:600, marginBottom:8 }}>METHOD · select a rail</div>
          <div style={{ display:"flex", flexDirection:"column", gap:6 }}>
            {(subject.saved_methods || []).map((m, i) => {
              const r = RAIL[m.rail] || { color: accent, label: m.rail };
              const eta = m.rail === "SWIFT" ? "1–3 days"
                        : m.rail === "SEPA"   ? "24h"
                        : m.rail === "SEPA-I" ? "<10s"
                        : m.rail === "ACH"    ? "1–2 days"
                        : m.rail === "FedNow" ? "<20s"
                        : m.rail === "FPS"    ? "<5s"
                        : "<30s";
              const isSel = i === selectedMethodIdx;
              const bank   = m.bank || (m.detail_label || "").split(" ·")[0] || "—";
              const acct   = m.account_masked || m.detail_label || "—";
              return (
                <div key={i}
                  onClick={() => setSelectedMethodIdx(i)}
                  style={{
                    display:"grid", gridTemplateColumns:"56px 1fr 64px", gap:12, alignItems:"center",
                    padding:"10px 12px",
                    border: `1px solid ${isSel ? r.color : "var(--term-line)"}`,
                    background: isSel ? accent + "10" : "var(--term-bg-pane)",
                    cursor:"pointer",
                    transition: "border-color .12s, background .12s",
                  }}>
                  <span className="term-rail-tag" style={{ color: r.color }}>{r.label}</span>
                  <div style={{ minWidth:0 }}>
                    <div style={{ fontSize:11.5, color:"var(--term-ink-1)", fontWeight:500, overflow:"hidden", textOverflow:"ellipsis", whiteSpace:"nowrap" }}>{bank}</div>
                    <div className="mono" style={{ fontSize:10.5, color:"var(--term-ink-2)", letterSpacing:"0.04em" }}>{acct}</div>
                    <div className="mono" style={{ fontSize:9.5, color:"var(--term-ink-3)", marginTop:2 }}>last used {m.last_used_iso}</div>
                  </div>
                  <span className="mono" style={{ textAlign:"right", fontSize:10.5, color:"var(--term-ink-3)", letterSpacing:"0.06em", textTransform:"uppercase" }}>{eta}</span>
                </div>
              );
            })}
            {(!subject.saved_methods || subject.saved_methods.length === 0) && (
              <div className="mono" style={{ padding:"16px 12px", fontSize:11, color:"var(--term-ink-3)", textAlign:"center", border:"1px dashed var(--term-line)" }}>
                no saved methods · add a payout account to pay this vendor
              </div>
            )}
          </div>
        </div>
        <div style={{ display:"grid", gridTemplateColumns:"1fr 1fr", gap:16 }}>
          <div>
            <div style={{ fontSize:9.5, letterSpacing:"0.16em", color:"var(--term-ink-3)", textTransform:"uppercase", fontWeight:600, marginBottom:8 }}>AMOUNT</div>
            <div style={{ padding:12, border:"1px solid var(--term-line)", background:"var(--term-bg-pane)", display:"flex", alignItems:"baseline", gap:8 }}>
              <input
                defaultValue={parseFloat(subject.due_amount || subject.outstanding || "0") > 0 ? Number(subject.due_amount || subject.outstanding).toLocaleString("en-US") : "0.00"}
                style={{ background:"transparent", border:"none", color:"var(--term-ink-1)", fontFamily:"JetBrains Mono", fontSize:22, fontWeight:500, width:"100%", outline:"none" }}
              />
              <span className="mono" style={{ fontSize:13, color:"var(--term-ink-2)" }}>{subject.due_currency || subject.currency || "USD"}</span>
            </div>
          </div>
          <div>
            <div style={{ fontSize:9.5, letterSpacing:"0.16em", color:"var(--term-ink-3)", textTransform:"uppercase", fontWeight:600, marginBottom:8 }}>REFERENCE</div>
            <div style={{ padding:12, border:"1px solid var(--term-line)", background:"var(--term-bg-pane)" }}>
              <input
                defaultValue={recipientLabel === "PERSON" ? "May invoice · contractor" : "INV-2026-228"}
                style={{ background:"transparent", border:"none", color:"var(--term-ink-1)", fontSize:13, width:"100%", outline:"none" }}
              />
            </div>
          </div>
        </div>
        <div style={{ display:"flex", gap:10, justifyContent:"flex-end", borderTop:"1px solid var(--term-line)", paddingTop:14 }}>
          <button className="term-util-btn">Save draft</button>
          <button style={{
            background: accent, border:`1px solid ${accent}`, color:"#000",
            height:32, padding:"0 18px", fontWeight:600, letterSpacing:"0.04em", fontSize:12, cursor:"pointer",
            fontFamily:"inherit",
          }}>Continue · 2FA</button>
        </div>
      </div>
    </div>
  );
}

function Queue({ items = [], selected, setSelected, mode }) {
  const accent = MODES.find((m) => m.id === mode).color;
  const total = items.reduce((a, x) => selected[x._idx] ? a + (parseFloat(x.amount) || 0) : a, 0);
  const count = items.filter((x) => selected[x._idx]).length;
  return (
    <div className="term-pane">
      <div className="term-pane-head">Pay queue · {mode} <span className="meta"><b>{count}</b> of {items.length} selected</span></div>
      <div style={{ padding:14, borderBottom:"1px solid var(--term-line)", background:"var(--term-bg-pane)" }}>
        <div style={{ fontSize:9.5, letterSpacing:"0.16em", color:"var(--term-ink-3)", textTransform:"uppercase", fontWeight:600 }}>BATCH TOTAL · USD-EQUIVALENT</div>
        <div className="mono" style={{ fontSize:22, color:"var(--term-ink-1)", fontWeight:500, letterSpacing:"-0.015em", lineHeight:1, marginTop:4 }}>{fmtUSD(total * 1.05)}</div>
        <div style={{ display:"flex", gap:14, fontFamily:"JetBrains Mono", fontSize:10.5, marginTop:8 }}>
          <span style={{ display:"inline-flex", alignItems:"center", gap:4 }}>
            <span style={{ color:"var(--term-ink-3)", fontSize:9.5, letterSpacing:"0.10em", textTransform:"uppercase" }}>RAILS</span>
            <span style={{ color:"var(--term-ink-1)" }}>{new Set(items.filter((x) => selected[x._idx]).map((x) => x.rail)).size}</span>
          </span>
          <span style={{ display:"inline-flex", alignItems:"center", gap:4 }}>
            <span style={{ color:"var(--term-ink-3)", fontSize:9.5, letterSpacing:"0.10em", textTransform:"uppercase" }}>CCYS</span>
            <span style={{ color:"var(--term-ink-1)" }}>{new Set(items.filter((x) => selected[x._idx]).map((x) => x.currency)).size}</span>
          </span>
          <span style={{ display:"inline-flex", alignItems:"center", gap:4 }}>
            <span style={{ color:"var(--term-ink-3)", fontSize:9.5, letterSpacing:"0.10em", textTransform:"uppercase" }}>EST FEE</span>
            <span style={{ color:"var(--term-ink-2)" }}>{fmtUSD(total * 0.0008)}</span>
          </span>
        </div>
      </div>
      <div className="term-pane-body">
        {items.length === 0 && <div style={{ padding:"20px 14px", fontSize:11, color:"var(--term-ink-3)" }}>No {mode} queued.</div>}
        {items.map((q) => {
          const r = RAIL[q.rail] || { color: accent, label: q.rail };
          const isSel = selected[q._idx];
          return (
            <div key={q._idx} onClick={() => setSelected((s) => ({ ...s, [q._idx]: !s[q._idx] }))}
              style={{ display:"grid", gridTemplateColumns:"18px 56px 1fr 90px", gap:8, alignItems:"center", padding:"0 14px", height:36, borderBottom:"1px solid var(--term-line-dim)", cursor:"pointer" }}>
              <span style={{ width:14, height:14, border:`1px solid ${isSel ? accent : "var(--term-ink-4)"}`, background: isSel ? accent : "transparent", display:"flex", alignItems:"center", justifyContent:"center", color:"#000", fontSize:10 }}>{isSel ? "✓" : ""}</span>
              <span className="term-rail-tag" style={{ color: r.color }}>{r.label}</span>
              <div style={{ minWidth:0 }}>
                <div style={{ fontSize:11.5, overflow:"hidden", textOverflow:"ellipsis", whiteSpace:"nowrap" }}>{q.counterparty}</div>
                <div className="mono" style={{ fontSize:9.5, color:"var(--term-ink-3)" }}>{q.reference}</div>
              </div>
              <span className="mono" style={{ textAlign:"right", fontSize:11 }}>{fmtCcy(q.amount, q.currency)}</span>
            </div>
          );
        })}
      </div>
      <div style={{ borderTop:"1px solid var(--term-line)", padding:14 }}>
        <button disabled={count === 0} style={{
          width:"100%", background: accent, border:`1px solid ${accent}`, color:"#000",
          height:36, padding:"0 18px", fontWeight:600, letterSpacing:"0.04em", fontSize:12,
          cursor: count === 0 ? "not-allowed" : "pointer", opacity: count === 0 ? 0.45 : 1,
          fontFamily:"inherit",
        }}>Authorize {mode} batch · {count}</button>
      </div>
    </div>
  );
}

function VendorList({ vendors, activeIdx, setActiveIdx }) {
  return (
    <div className="term-pane">
      <div className="term-pane-head">Vendors · saved counterparties <span className="meta"><b>{vendors.length}</b></span></div>
      <div className="term-pane-body">
        <div style={{ display:"grid", gridTemplateColumns:"24px 1fr 70px 60px", gap:8, alignItems:"center", padding:"0 14px", height:22, fontSize:9.5, letterSpacing:"0.12em", color:"var(--term-ink-2)", textTransform:"uppercase", background:"var(--term-bg)", position:"sticky", top:0, zIndex:1, fontWeight:600, borderBottom:"1px solid var(--term-line-dim)" }}>
          <span>CC</span><span>VENDOR</span><span style={{ textAlign:"right" }}>DUE</span><span style={{ textAlign:"right" }}>TERMS</span>
        </div>
        {vendors.map((p, i) => {
          const due = parseFloat(p.due_amount) || 0;
          const isActive = activeIdx === i;
          return (
            <div key={i} className="term-has-ctx" onClick={() => setActiveIdx(i)} style={{
              display:"grid", gridTemplateColumns:"24px 1fr 70px 60px", gap:8, alignItems:"center",
              padding:"0 14px", height:36, borderBottom:"1px solid var(--term-line-dim)",
              cursor:"pointer", position:"relative",
              background: isActive ? "var(--term-bg-pane-2)" : "transparent",
            }}>
              {isActive && <span style={{ position:"absolute", left:0, top:0, bottom:0, width:2, background:"#4A8CFF" }} />}
              <span className="mono" style={{ fontSize:9.5, color:"var(--term-ink-3)", textAlign:"center", letterSpacing:"0.04em" }}>{p.iso}</span>
              <span style={{ display:"flex", flexDirection:"column", gap:2, minWidth:0 }}>
                <span style={{ fontSize:11.5, color:"var(--term-ink-1)", overflow:"hidden", textOverflow:"ellipsis", whiteSpace:"nowrap" }}>{p.name}</span>
                <span className="mono" style={{ fontSize:9.5, color:"var(--term-ink-3)", letterSpacing:"0.04em" }}>{p.channel_id} · {p.saved_methods.length} method{p.saved_methods.length > 1 ? "s" : ""}</span>
              </span>
              <span className="mono" style={{ textAlign:"right", fontSize:11, color: due > 0 ? "var(--term-amber)" : "var(--term-ink-3)" }}>{due > 0 ? fmtUSD(due / 1000) + "K" : "—"}</span>
              <span className="mono" style={{ textAlign:"right", fontSize:10, color:"var(--term-ink-3)" }}>{p.payment_terms}</span>
              <span style={{ position:"absolute", right:6, top:"50%", transform:"translateY(-50%)", background:"var(--term-bg-pane-2)" }}>
                <ItemActions actions={CTX.payee()} />
              </span>
            </div>
          );
        })}
      </div>
    </div>
  );
}

function PeopleList({ people, activeIdx, setActiveIdx }) {
  return (
    <div className="term-pane">
      <div className="term-pane-head">People · employees + contractors <span className="meta"><b>{people.length}</b></span></div>
      <div style={{ padding:"10px 14px", borderBottom:"1px solid var(--term-line-dim)" }}>
        <input placeholder="Search by name, @handle, email…" style={{ width:"100%", padding:"7px 10px", background:"var(--term-bg-base)", border:"1px solid var(--term-line)", color:"var(--term-ink-1)", fontFamily:"inherit", fontSize:11.5, outline:"none" }} />
      </div>
      <div className="term-pane-body">
        <div style={{ display:"grid", gridTemplateColumns:"24px 1fr 80px", gap:8, alignItems:"center", padding:"0 14px", height:22, fontSize:9.5, letterSpacing:"0.12em", color:"var(--term-ink-2)", textTransform:"uppercase", background:"var(--term-bg)", position:"sticky", top:0, zIndex:1, fontWeight:600, borderBottom:"1px solid var(--term-line-dim)" }}>
          <span>CC</span><span>PERSON</span><span style={{ textAlign:"right" }}>OWED</span>
        </div>
        {people.map((p, i) => {
          const owed = parseFloat(p.outstanding) || 0;
          const isActive = activeIdx === i;
          return (
            <div key={i} className="term-has-ctx" onClick={() => setActiveIdx(i)} style={{
              display:"grid", gridTemplateColumns:"24px 1fr 80px", gap:8, alignItems:"center",
              padding:"0 14px", height:36, borderBottom:"1px solid var(--term-line-dim)",
              cursor:"pointer", position:"relative",
              background: isActive ? "var(--term-bg-pane-2)" : "transparent",
            }}>
              {isActive && <span style={{ position:"absolute", left:0, top:0, bottom:0, width:2, background:"#A78BFA" }} />}
              <span className="mono" style={{ fontSize:9.5, color:"var(--term-ink-3)", textAlign:"center", letterSpacing:"0.04em" }}>{p.iso}</span>
              <span style={{ display:"flex", flexDirection:"column", gap:2, minWidth:0 }}>
                <span style={{ fontSize:11.5, color:"var(--term-ink-1)", overflow:"hidden", textOverflow:"ellipsis", whiteSpace:"nowrap" }}>{p.name}</span>
                <span className="mono" style={{ fontSize:9.5, color:"var(--term-ink-3)", letterSpacing:"0.04em" }}>{p.handle} · {p.role}</span>
              </span>
              <span className="mono" style={{ textAlign:"right", fontSize:11, color: owed > 0 ? "var(--term-amber)" : "var(--term-ink-3)" }}>{owed > 0 ? fmtCcy(owed, p.currency) : "—"}</span>
            </div>
          );
        })}
      </div>
    </div>
  );
}

function BillsList({ bills, activeIdx, setActiveIdx }) {
  const stColor = { scheduled:"var(--term-green)", manual:"var(--term-amber)", overdue:"var(--term-red)", paused:"var(--term-ink-3)" };
  return (
    <div className="term-pane">
      <div className="term-pane-head">Bills + subscriptions <span className="meta"><b>{bills.length}</b> active</span></div>
      <div className="term-pane-body">
        <div style={{ display:"grid", gridTemplateColumns:"24px 1fr 70px 80px", gap:8, alignItems:"center", padding:"0 14px", height:22, fontSize:9.5, letterSpacing:"0.12em", color:"var(--term-ink-2)", textTransform:"uppercase", background:"var(--term-bg)", position:"sticky", top:0, zIndex:1, fontWeight:600, borderBottom:"1px solid var(--term-line-dim)" }}>
          <span>CC</span><span>BILL</span><span style={{ textAlign:"right" }}>NEXT</span><span style={{ textAlign:"right" }}>AMOUNT</span>
        </div>
        {bills.map((b, i) => {
          const isActive = activeIdx === i;
          return (
            <div key={b.id} className="term-has-ctx" onClick={() => setActiveIdx(i)} style={{
              display:"grid", gridTemplateColumns:"24px 1fr 70px 80px", gap:8, alignItems:"center",
              padding:"0 14px", height:36, borderBottom:"1px solid var(--term-line-dim)",
              cursor:"pointer", position:"relative",
              background: isActive ? "var(--term-bg-pane-2)" : "transparent",
            }}>
              {isActive && <span style={{ position:"absolute", left:0, top:0, bottom:0, width:2, background:"#06B6D4" }} />}
              <span className="mono" style={{ fontSize:9.5, color:"var(--term-ink-3)", textAlign:"center", letterSpacing:"0.04em" }}>{b.iso}</span>
              <span style={{ display:"flex", flexDirection:"column", gap:2, minWidth:0 }}>
                <span style={{ fontSize:11.5, color:"var(--term-ink-1)", overflow:"hidden", textOverflow:"ellipsis", whiteSpace:"nowrap" }}>{b.name}</span>
                <span className="mono" style={{ fontSize:9.5, color:"var(--term-ink-3)", display:"flex", alignItems:"center", gap:6 }}>
                  <span style={{ width:5, height:5, background: stColor[b.status] }} />
                  {b.category} · {b.autopay ? "autopay" : "manual"}
                </span>
              </span>
              <span className="mono" style={{ textAlign:"right", fontSize:10, color:"var(--term-ink-3)" }}>{(b.next_due_iso || "").slice(5)}</span>
              <span className="mono" style={{ textAlign:"right", fontSize:11 }}>{fmtCcy(b.amount, b.currency)}</span>
            </div>
          );
        })}
      </div>
    </div>
  );
}

function BillComposer({ bill }) {
  const stColor = { scheduled:"#2CCB7E", manual:"#F5A524", overdue:"#FF5F6B", paused:"var(--term-ink-3)" };
  return (
    <div className="term-pane">
      <div className="term-pane-head">Bill schedule <span className="meta">{bill.name}</span></div>
      <div style={{ padding:18, display:"flex", flexDirection:"column", gap:16, overflow:"auto" }}>
        <div style={{ display:"grid", gridTemplateColumns:"1fr 1fr", gap:16 }}>
          <Stat label="NEXT DUE"  value={bill.next_due_iso} mono />
          <Stat label="AMOUNT"    value={fmtCcy(bill.amount, bill.currency)} mono />
          <Stat label="CATEGORY"  value={bill.category} />
          <Stat label="STATUS"    value={(bill.status || "").toUpperCase()} color={stColor[bill.status]} />
        </div>
        <div>
          <div style={{ fontSize:9.5, letterSpacing:"0.16em", color:"var(--term-ink-3)", textTransform:"uppercase", fontWeight:600, marginBottom:8 }}>PAY METHOD</div>
          <div className="mono" style={{ padding:12, border:"1px solid var(--term-line)", background:"var(--term-bg-pane)", fontSize:12 }}>
            {bill.method_rail} · {bill.method_detail_label}
          </div>
        </div>
        <div>
          <div style={{ fontSize:9.5, letterSpacing:"0.16em", color:"var(--term-ink-3)", textTransform:"uppercase", fontWeight:600, marginBottom:8 }}>AUTOPAY</div>
          <div style={{ display:"flex", gap:8 }}>
            <button style={{ flex:1, padding:10, fontFamily:"inherit", fontSize:12, background: bill.autopay ? "#06B6D418" : "var(--term-bg-base)", border:`1px solid ${bill.autopay ? "#06B6D4" : "var(--term-line)"}`, color: bill.autopay ? "#06B6D4" : "var(--term-ink-2)", cursor:"pointer", fontWeight:600 }}>{bill.autopay ? "✓ ON · auto-charge" : "OFF · turn on autopay"}</button>
            <button style={{ flex:1, padding:10, fontFamily:"inherit", fontSize:12, background:"var(--term-bg-base)", border:"1px solid var(--term-line)", color:"var(--term-ink-2)", cursor:"pointer" }}>Edit rule</button>
          </div>
        </div>
        <div style={{ display:"flex", gap:10, justifyContent:"flex-end", borderTop:"1px solid var(--term-line)", paddingTop:14 }}>
          <button className="term-util-btn">Skip next</button>
          <button className="term-util-btn">Pay early</button>
          <button style={{ background:"#06B6D4", border:"1px solid #06B6D4", color:"#000", height:32, padding:"0 18px", fontWeight:600, letterSpacing:"0.04em", fontSize:12, cursor:"pointer", fontFamily:"inherit" }}>Save schedule</button>
        </div>
      </div>
    </div>
  );
}

function Stat({ label, value, color, mono }) {
  return (
    <div>
      <div style={{ fontSize:9.5, letterSpacing:"0.16em", color:"var(--term-ink-3)", textTransform:"uppercase", fontWeight:600, marginBottom:4 }}>{label}</div>
      <div className={mono ? "mono" : ""} style={{ padding:"10px 12px", border:"1px solid var(--term-line)", background:"var(--term-bg-pane)", fontSize: mono ? 14 : 13, color: color || "var(--term-ink-1)", fontWeight: mono ? 500 : 400 }}>{value}</div>
    </div>
  );
}

export default function PayScreen({ token }) {
  const { vendors } = useVendors(token);
  const { bills }   = useBills(token);
  const { people }  = usePeople(token);
  const { queue }   = usePayQueue();
  const [mode, setMode] = useState("vendors");
  const [vendorIdx, setVendorIdx] = useState(0);
  const [personIdx, setPersonIdx] = useState(0);
  const [billIdx, setBillIdx]     = useState(0);

  const queueIndexed = queue.map((q, _idx) => ({ ...q, _idx }));
  const [selected, setSelected] = useState(() => {
    const s = {};
    queueIndexed.forEach((q) => { s[q._idx] = !!q.sel; });
    return s;
  });

  const filteredQueue = queueIndexed.filter((q) => q.mode === mode);

  return (
    <div style={{ display:"flex", flexDirection:"column", height:"calc(100vh - 40px - 30px)" }}>
      <ModeBar mode={mode} setMode={setMode} />
      <div style={{ flex:1, minHeight:0, display:"grid", gridTemplateColumns:"360px 1fr 380px" }}>
        {mode === "vendors" && (
          <>
            <VendorList vendors={vendors} activeIdx={vendorIdx} setActiveIdx={setVendorIdx} />
            <Composer subject={vendors[vendorIdx]} accent="#4A8CFF" recipientLabel="VENDOR" />
            <Queue items={filteredQueue} selected={selected} setSelected={setSelected} mode={mode} />
          </>
        )}
        {mode === "p2p" && (
          <>
            <PeopleList people={people} activeIdx={personIdx} setActiveIdx={setPersonIdx} />
            <Composer subject={people[personIdx]} accent="#A78BFA" recipientLabel="PERSON" />
            <Queue items={filteredQueue} selected={selected} setSelected={setSelected} mode={mode} />
          </>
        )}
        {mode === "bulk" && (
          <div style={{ gridColumn:"1 / -1", display:"flex", minHeight:0 }}>
            <BulkMode />
          </div>
        )}
        {mode === "bills" && (
          <>
            <BillsList bills={bills} activeIdx={billIdx} setActiveIdx={setBillIdx} />
            <BillComposer bill={bills[billIdx]} />
            <Queue items={filteredQueue} selected={selected} setSelected={setSelected} mode={mode} />
          </>
        )}
        {mode === "global" && (
          <div style={{ gridColumn:"1 / -1", display:"grid", gridTemplateColumns:"1fr 380px", minHeight:0 }}>
            <SendPayout token={token} />
            <PayoutHistory token={token} />
          </div>
        )}
      </div>
    </div>
  );
}
